// One page at a time, by path, with history.pushState. A route names its layout: the landing page draws its own
// header, the cabinet's pages sit inside the cabinet's header and menu. A page that needs a session sends a visitor
// without one to identity first, as AuthorizeRouteView did.

import { h } from './dom.ts';
import { t } from './i18n.ts';
import { log } from './logs.ts';
import { on } from './state.ts';

/** What a page draws: one element or several. */
export type View = Node | readonly Node[];

export type Layout = 'none' | 'cabinet';

export interface NavigateOptions {
    reload?: boolean;
    replace?: boolean;
}

export interface PageContext {
    active: boolean;
    q(name: string): string | null;
    /** Where the page draws. */
    attach(element: HTMLElement): void;
    render(view: () => View): void;
    rerender(): void;
    title(key: string, fallback?: string | null): void;
    onDestroy(fn: () => void): void;
    go(url: string, options?: NavigateOptions): void;
    run<T>(fn: () => Promise<T>): Promise<T | undefined>;
    destroy(): void;
}

interface Route {
    mount: (ctx: PageContext) => void;
    layout: Layout;
    auth: boolean;
}

export interface LayoutHost {
    show(layout: Layout, ctx: PageContext): HTMLElement;
    isAuthenticated(): boolean;
    login(): void;
}

const routes = new Map<string, Route>();
let notFound: Route | null = null;

let current: PageContext | null = null;

let host: LayoutHost | null = null;

const normalise = (path: string): string => (path.replace(/\/+$/, '') || '/').toLowerCase();

export function route(path: string, mount: (ctx: PageContext) => void, options: { layout?: Layout; auth?: boolean } = {}): void {
    const entry: Route = { mount, layout: options.layout || 'cabinet', auth: !!options.auth };

    if (path === '*') notFound = entry;
    else routes.set(normalise(path), entry);
}

export function navigate(url: string, { reload = false, replace = false }: NavigateOptions = {}): void {
    const target = new URL(url, location.href);

    if (reload || target.origin !== location.origin) {
        location.href = target.href;

        return;
    }

    if (target.pathname === location.pathname && target.search === location.search && target.hash && !replace) {
        history.pushState(null, '', target.hash);
        document.getElementById(decodeURIComponent(target.hash.slice(1)))?.scrollIntoView({ behavior: 'smooth' });

        return;
    }

    history[replace ? 'replaceState' : 'pushState'](null, '', target.pathname + target.search + target.hash);
    renderRoute();
}

function fatal(error: unknown, view: HTMLElement | null): void {
    log.error('Unhandled UI exception', error, undefined, 'Lify.Influencer.Components.Routes');

    if (current) current.active = false;

    view?.replaceChildren(h('p', { role: 'alert', class: 'fatal' }, 'Something went wrong. Please reload the page.'));
}

function createContext(): PageContext {
    const query = new URLSearchParams(location.search);
    const cleanups: Array<() => void> = [];
    let renderer: (() => View) | null = null;
    let titleKey: string | null = null;
    let titleFallback: string | null = null;
    let view: HTMLElement | null = null;

    const ctx: PageContext = {
        active: true,

        q: name => query.get(name),

        attach(element) {
            view = element;
        },

        render(viewFn) {
            renderer = viewFn;
            ctx.rerender();
        },

        rerender() {
            if (!ctx.active || !renderer || !view) return;

            // Whatever field the person is in stays focused across a redraw, as long as it carries the same name.
            const focused = document.activeElement;
            const input = focused instanceof HTMLInputElement && view.contains(focused) ? focused : null;
            const focusName = input ? input.className : null;
            const selection = input && typeof input.selectionStart === 'number'
                ? [input.selectionStart, input.selectionEnd ?? input.selectionStart] as const
                : null;
            const scroller = view.querySelector('.page-content, .background');
            const scrollTop = scroller ? scroller.scrollTop : 0;

            try {
                const drawn = renderer();
                view.replaceChildren(...(Array.isArray(drawn) ? drawn : [drawn as Node]));
            } catch (error) {
                fatal(error, view);

                return;
            }

            // A redraw must not throw the person back to the top of a scrolled page.
            const newScroller = view.querySelector('.page-content, .background');

            if (newScroller && scrollTop) newScroller.scrollTop = scrollTop;

            if (focusName) {
                const again = view.querySelector<HTMLInputElement>(`input.${CSS.escape(focusName.split(' ')[0]!)}`);

                if (again) {
                    again.focus({ preventScroll: true });

                    try {
                        if (selection) again.setSelectionRange(selection[0], selection[1]);
                    } catch {
                        // A number input keeps no selection.
                    }
                }
            }

            if (titleKey) document.title = t(titleKey) || titleFallback || 'Lify Influencer';
        },

        title(key, fallback) {
            titleKey = key;
            titleFallback = fallback ?? null;
            document.title = t(key) || fallback || 'Lify Influencer';
        },

        onDestroy(fn) {
            cleanups.push(fn);
        },

        go(url, options) {
            if (ctx.active) navigate(url, options);
        },

        async run<T>(fn: () => Promise<T>): Promise<T | undefined> {
            try {
                return await fn();
            } catch (error) {
                if (ctx.active) fatal(error, view);

                return undefined;
            }
        },

        destroy() {
            ctx.active = false;

            for (const cleanup of cleanups.splice(0)) {
                try {
                    cleanup();
                } catch (error) {
                    log.error('Page cleanup failed', error);
                }
            }
        }
    };

    return ctx;
}

function renderRoute(): void {
    current?.destroy();

    const entry = routes.get(normalise(location.pathname)) || notFound;
    const ctx = createContext();

    current = ctx;

    if (!entry || !host) return;

    if (entry.auth && !host.isAuthenticated()) {
        host.login();

        return;
    }

    const view = host.show(entry.layout, ctx);

    ctx.attach(view);

    try {
        entry.mount(ctx);
    } catch (error) {
        fatal(error, view);
    }

    if (location.hash) {
        requestAnimationFrame(() => document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView());
    }
}

export function startRouter(layoutHost: LayoutHost): void {
    host = layoutHost;
    window.addEventListener('popstate', renderRoute);
    on('languageChanged', () => current?.rerender());

    // Same-origin links are navigated here, not by the browser: the cabinet stays one page.
    document.addEventListener('click', event => {
        const link = (event.target as Element | null)?.closest?.<HTMLAnchorElement>('a[href]');

        if (!link || link.target || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

        const url = new URL(link.href, location.href);

        if (url.origin !== location.origin || link.hasAttribute('download')) return;

        event.preventDefault();
        navigate(url.href);
    });

    renderRoute();
}

export function rerenderCurrent(): void {
    current?.rerender();
}
